import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { Download, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { trackFormStart, trackLead } from "@/lib/analytics";

const PDF_URL = "/guides/kore-starter-guide.pdf";

export default function LeadMagnet() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  const markStarted = () => {
    if (started) return;
    setStarted(true);
    trackFormStart({ form_name: "free_guide" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/newsletter", {
        email: form.email,
        name: form.name || null,
        source: "lead_magnet_starter_guide",
      });
      setDone(true);
      trackLead({ form_name: "free_guide", enquiry_type: "lead_magnet" });
      toast.success("Sent — your download is ready below.");
      window.open(PDF_URL, "_blank", "noopener");
    } catch (err) {
      toast.error(errText(err) || "We couldn't send that just now — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Seo
        title="The Kore Starter Guide — free 6-habit field guide"
        description="Free download — the six habits that quietly rebuild bodies. A short, honest field-guide to your first six weeks of training, from the coaching team at Kore Wellbeing."
        path="/free-guide"
      />
      <section className="container-kore pt-16 pb-8 max-w-4xl">
        <span className="eyebrow">Free download</span>
        <h1 className="display-1 mt-4 text-kore-forest">The Kore Starter Guide.</h1>
        <p className="mt-6 text-kore-ink/75 text-lg font-light max-w-2xl leading-relaxed">
          Six habits that change everything — a quiet, honest field-guide to your first six weeks of training, from the coaching team at Kore Wellbeing.
        </p>
      </section>

      <section className="container-kore pb-24 grid md:grid-cols-2 gap-12 items-start max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="bg-kore-forest text-kore-warm rounded-3xl p-10 md:p-12 shadow-lift"
        >
          <div className="text-[11px] uppercase tracking-[0.24em] text-kore-light">Inside</div>
          <ul className="mt-6 space-y-4 text-sm text-kore-warm/85">
            {[
              "The two-session-a-week strength habit that quietly rebuilds bodies",
              "A ten-minute mobility routine that fits before your first coffee",
              "The three recovery levers that beat every supplement on the market",
              "Nutrition without tracking, weighing or willpower",
              "Why accountability out-performs motivation every time",
              "The community habit that keeps every member coming back",
            ].map((it, i) => (
              <li key={it} className="flex gap-4">
                <span className="text-kore-light text-xs mt-1">0{i + 1}</span>
                <span>{it}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 pt-8 border-t border-kore-light/20 text-xs text-kore-warm/70 leading-relaxed">
            A short, printable A4 booklet from the team at Kore. No fluff, no shouting, no sales — just what actually works.
          </div>
        </motion.div>

        <div className="bg-white rounded-3xl p-10 shadow-soft">
          {!done ? (
            <form onSubmit={submit} onFocus={markStarted} data-testid="lead-magnet-form" className="space-y-5">
              <div>
                <span className="eyebrow">First name</span>
                <input
                  required
                  data-testid="lead-magnet-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent"
                />
              </div>
              <div>
                <span className="eyebrow">Email</span>
                <input
                  required
                  type="email"
                  data-testid="lead-magnet-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full border-b border-kore-ink/20 py-2 focus:outline-none focus:border-kore-gold bg-transparent"
                />
              </div>
              <button disabled={busy} data-testid="lead-magnet-submit" className="btn-primary w-full mt-6">
                {busy ? "Sending…" : (<>Send me the guide <Download size={14} /></>)}
              </button>
              <p className="text-[11px] text-kore-ink/50 leading-relaxed mt-4">
                By downloading, you're joining our monthly journal. Unsubscribe any time in a click. We never share your email.
              </p>
            </form>
          ) : (
            <div data-testid="lead-magnet-success" className="text-center py-6">
              <div className="w-14 h-14 mx-auto rounded-full bg-kore-light flex items-center justify-center">
                <Download size={22} className="text-kore-forest" />
              </div>
              <h3 className="font-display text-3xl mt-6 text-kore-forest">You're in.</h3>
              <p className="mt-3 text-kore-ink/70">Your download should have opened in a new tab. If it didn't, click below.</p>
              <a href={PDF_URL} target="_blank" rel="noreferrer" className="btn-primary mt-8 inline-flex" data-testid="lead-magnet-download">
                Download the guide <Download size={14} />
              </a>
              <div className="mt-10 pt-6 border-t border-kore-gold/20">
                <Link to="/book" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-kore-forest border-b border-kore-gold pb-1">
                  Or book your first class <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
